import "./index.less";
import React from "react";
import { Table, Tooltip } from "antd";
import { QuestionCircleFilled } from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";
import { Observer, observer } from "mobx-react-lite";
import { ShopOverviewResult } from "@/services/api/order";
import ShopInfoColumn from "./components/ShopInfoColumn";
import TotalLogisticsWarningOrdersColumn from "./components/TotalLogisticsWarningOrdersColumn";
import type { TableColumnsType } from "antd";
import { useShopInfoStore } from "../../store";
import { useGlobalStore } from "@/store/hooks";
import { ShippingDeadlineShopTypes } from "@/utils/ShopUtils";
import moment from "moment";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import QuickEnum from "@/scenes/OrderManagement/enum/QuickEnum";
import AuthTokenHolder from "@/services/http/AuthTokenHolder";
import { FD_BASE_URL } from "@/config";

interface OrderOverViewTableProps {
    isSupplier: boolean;
}

const useColumns = (isSupplier?: boolean) => {
    const history = useHistory();
    const globalStore = useGlobalStore();

    function buildIframeUrl(params: Record<string, any>) {
        const search = new URLSearchParams();
        const token = AuthTokenHolder?.getToken?.();

        search.set("_token_", token);

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                search.set(key, String(value));
            }
        });

        return `${FD_BASE_URL}/order/list?${search.toString()}`;
    }

    const columns: TableColumnsType<ShopOverviewResult> = [
        {
            title: (
                <span>
                    店铺名称&nbsp;
                    <Link to="/setting/shop/add">添加（关联店铺）</Link>
                </span>
            ),
            dataIndex: "shopName",
            width: 220,
            render(_, shopOverviewResult) {
                return <ShopInfoColumn shopOverviewResult={shopOverviewResult} />;
            },
        },
        {
            title: "待发货",
            width: 160,
            render(_, { totalPendingShipmentOrders, shopId }) {
                return (
                    <Observer>
                        {() => {
                            const shopSetting = globalStore.shopSettingMapping.get(shopId);

                            return (
                                <div>
                                    {totalPendingShipmentOrders ? (
                                        globalStore.isIframe ? (
                                            <a
                                                target="_blank"
                                                href={buildIframeUrl({
                                                    shopId,
                                                    tab: OrderTab.PENDING_SHIPMENT,
                                                })}
                                            >
                                                {totalPendingShipmentOrders}
                                            </a>
                                        ) : (
                                            <Link to={`/order/list?shopId=${shopId}&tab=${OrderTab.PENDING_SHIPMENT}`}>
                                                {totalPendingShipmentOrders}
                                            </Link>
                                        )
                                    ) : (
                                        <span className="text-gray">{totalPendingShipmentOrders}</span>
                                    )}

                                    {shopSetting?.lastSyncOrderTime ? (
                                        <p className="text-gray small mt5">
                                            已同步至{moment(shopSetting.lastSyncOrderTime).format("MM-DD HH:mm")}
                                        </p>
                                    ) : null}
                                </div>
                            );
                        }}
                    </Observer>
                );
            },
        },
        {
            title: "异常",
            render(_, { hasAbnormalOrders, shopId }) {
                if (!hasAbnormalOrders) return null;

                return globalStore.isIframe ? (
                    <a
                        target="_blank"
                        href={buildIframeUrl({
                            shopId,
                            tab: OrderTab.ABNORMAL,
                        })}
                    >
                        <span className="label label-danger">有</span>
                    </a>
                ) : (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            history.push(`/order/list?shopId=${shopId}&tab=${OrderTab.ABNORMAL}`);
                        }}
                    >
                        <span className="label label-danger">有</span>
                    </a>
                );
            },
        },
        {
            title: (
                <div>
                    <span>物流异常预警</span>
                    <Tooltip title="订单发货后xx小时仍无物流信息时会预警">
                        <QuestionCircleFilled className="ml5 mr5" style={{ color: "#337ab7" }} />
                    </Tooltip>
                    (<Link to="/setting/orderLogisticsWarning">去设置</Link>)
                </div>
            ),
            dataIndex: "totalLogisticsWarningOrders",
            render(totalLogisticsWarningOrders, shopOverviewResult) {
                return <TotalLogisticsWarningOrdersColumn shopOverviewResult={shopOverviewResult} />;
            },
        },
        {
            title: (
                <div>
                    <span>即将发货超时</span>(<Link to="/setting/shop">去设置</Link>)
                </div>
            ),
            render(_, { shippingDeadlineOrderCount, shopId }) {
                const shop = globalStore.shopMapping.get(shopId);

                if (!ShippingDeadlineShopTypes.includes(shop?.type)) return null;

                if (!shippingDeadlineOrderCount) {
                    return <span className="text-gray">{shippingDeadlineOrderCount}</span>;
                }

                return globalStore.isIframe ? (
                    <a
                        target="_blank"
                        href={buildIframeUrl({
                            shopId,
                            tab: OrderTab.PENDING_SHIPMENT,
                            quick: QuickEnum.QUICK_DELIVERY_TIMEOUT,
                        })}
                    >
                        {shippingDeadlineOrderCount}
                    </a>
                ) : (
                    <Link
                        to={`/order/list?shopId=${shopId}&tab=${OrderTab.PENDING_SHIPMENT}&quick=${QuickEnum.QUICK_DELIVERY_TIMEOUT}`}
                    >
                        {shippingDeadlineOrderCount}
                    </Link>
                );
            },
        },
        !isSupplier
            ? {
                  title: "退款中",
                  render(_, { totalRefundingOrders, shopId }) {
                      if (!totalRefundingOrders) {
                          return <span className="text-gray">{totalRefundingOrders}</span>;
                      }

                      return globalStore.isIframe ? (
                          <a
                              target="_blank"
                              href={buildIframeUrl({
                                  shopId,
                                  tab: OrderTab.PENDING_SHIPMENT_REFUNDING,
                              })}
                          >
                              {totalRefundingOrders}
                          </a>
                      ) : (
                          <Link to={`/order/list?shopId=${shopId}&tab=${OrderTab.PENDING_SHIPMENT_REFUNDING}`}>
                              {totalRefundingOrders}
                          </Link>
                      );
                  },
              }
            : undefined,
    ];

    return columns.filter((it) => !!it);
};

const OrderOverViewTable: React.FC<OrderOverViewTableProps> = ({ isSupplier = false }) => {
    const columns = useColumns(isSupplier);
    const store = useShopInfoStore();

    return (
        <div className="panel-overview-table">
            <Table
                columns={columns}
                rowKey="shopId"
                pagination={false}
                dataSource={store.shopOverviewResults}
                locale={{ emptyText: <span /> }}
            />
        </div>
    );
};

export default observer(OrderOverViewTable);
